import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  SHARED_OFFICE_HOURS_SESSION_PATH,
  SHARED_SOLUTION_SESSION_PATH,
} from '@backrs_ui/grow-ui/src/components/PageShell/sharedSessionPaths';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { useSharedBlueprintData } from '../hooks/useSharedBlueprintData';
import { useSharedTrackAssessmentData } from '../hooks/useSharedTrackAssessmentData';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  activateSharedSession,
  selectIsSharedSessionActive,
} from '../redux/sharedSession/sharedSession';
import { useExchangeSessionQuery } from '../redux/sharedSession/sharedSession.api';

type SharedSessionProviderProps = Readonly<{
  children: ReactNode;
}>;

export const SharedSessionProvider = ({ children }: SharedSessionProviderProps) => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const isSharedSessionActive = useAppSelector(selectIsSharedSessionActive);

  // Session shares (office hours / solution sessions) don't carry blueprint or track-assessment
  // data, so skip those fetches.
  const isSessionShare =
    SHARED_OFFICE_HOURS_SESSION_PATH.test(pathname) || SHARED_SOLUTION_SESSION_PATH.test(pathname);

  const { data: exchangeSessionData, isLoading: isExchangeSessionLoading } =
    useExchangeSessionQuery({ shareSessionId: uuid || '' }, { skip: !uuid });

  useEffect(() => {
    // redirect to home if the session is not active or the API did not return an access token
    if (!isExchangeSessionLoading) {
      // NOTE: we can also check if the session is active here -- need to modify BE API to return the session status
      if (!exchangeSessionData?.share_session_token) {
        navigate(APP_ROUTE.HOME);
      } else {
        dispatch(
          activateSharedSession({
            token: exchangeSessionData.share_session_token,
            ownerFirstName: exchangeSessionData.ownerFirstName,
            ownerLastName: exchangeSessionData.ownerLastName,
            ownerProfileImageUrl: exchangeSessionData.ownerProfileImageUrl,
            resourceType: exchangeSessionData.resource_type,
            resourceId: exchangeSessionData.resource_id,
          }),
        );
      }
    }
  }, [isExchangeSessionLoading, exchangeSessionData, navigate, dispatch]);

  // fetch, calculate, activate and set the shared blueprint data
  const {
    isError: isBlueprintError,
    isLoading: isBlueprintLoading,
    error: blueprintError,
  } = useSharedBlueprintData({
    accessToken: exchangeSessionData?.share_session_token || '',
    skip: isSessionShare,
  });

  useEffect(() => {
    if (isBlueprintError) {
      console.error('Failed to load shared blueprint data:', blueprintError);
    }
  }, [isBlueprintError, blueprintError]);

  // fetch, activate and set the shared track assessment data
  useSharedTrackAssessmentData({
    accessToken: exchangeSessionData?.share_session_token || '',
    skip: isSessionShare,
  });

  if (isExchangeSessionLoading || !isSharedSessionActive || isBlueprintLoading) {
    return <FullScreenLoading />;
  }

  if (isBlueprintError) {
    return (
      <Box sx={errorContainerSx}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {appStrings.modules.sharedSession.blueprintLoadError}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

// --- Styles ---

const errorContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  padding: 4,
};
